import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { apiCall } from '../utils/api';
import { useAuth } from './AuthContext';

/**
 * PlatformSettingsContext — surfaces the small set of global toggles
 * the super-admin can flip that must be respected by every UI form.
 * Introduced Feb 25, 2026 (iter173) for the "shift pay-rate editing"
 * gate.
 *
 * Hydration:
 *   - On login (user state transitions non-null) we hit
 *     GET /api/platform-settings/public and cache the flag bag.
 *   - Consumers use `usePlatformSettings()` and read
 *     `payRateEditingEnabled` to gate their inputs.
 *   - The provider polls once per minute so an admin flip propagates
 *     to already-open tabs within ~60s without needing a hard refresh.
 */
const DEFAULTS = { payRateEditingEnabled: false, lockedRateDisplay: 'agency', maxVisibilityRadiusKm: null, requireLocationForJobs: false };

const PlatformSettingsContext = createContext({
  ...DEFAULTS,
  refresh: async () => {},
});

export function PlatformSettingsProvider({ children }) {
  const { user } = useAuth();
  const [flags, setFlags] = useState(DEFAULTS);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const r = await apiCall('/api/platform-settings/public');
      setFlags({
        payRateEditingEnabled: !!r?.pay_rate_editing_enabled,
        lockedRateDisplay: r?.locked_rate_display === 'hidden' ? 'hidden' : 'agency',
        maxVisibilityRadiusKm: r?.max_visibility_radius_km ?? null,
        requireLocationForJobs: !!r?.require_location_for_jobs,
      });
    } catch (_e) {
      // Silent — keep last-known-good defaults so a transient blip
      // never disables the entire creation UI mid-session.
    }
  }, [user]);

  useEffect(() => {
    refresh();
    if (!user) return undefined;
    const t = setInterval(refresh, 60_000); // 60s cross-tab drift
    return () => clearInterval(t);
  }, [user, refresh]);

  return (
    <PlatformSettingsContext.Provider value={{ ...flags, refresh }}>
      {children}
    </PlatformSettingsContext.Provider>
  );
}

export function usePlatformSettings() {
  return useContext(PlatformSettingsContext);
}
